import React, { useState, useEffect, useRef } from 'react'
import {
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Collapse,
  Alert,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputAdornment,
  Link,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Bathtub as BathtubIcon,
  Kitchen as KitchenIcon,
  Bolt as BoltIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Park as ParkIcon,
  Build as BuildIcon,
} from '@mui/icons-material'

interface RepairCategory {
  name: string
  imageUrl: string
  jobTypes: string[]
  expanded?: boolean
}

// Repair categories data - exactly matching C# model
const repairCategories: RepairCategory[] = [
  {
    name: "Bathroom",
    imageUrl: "/images/bathtub.jpg",
    jobTypes: [
      "Tap", "Leaks", "Mould", "Toilet", "Bath & Shower",
      "Sink & Basins", "Tiling", "Extractor Fan", "Lighting",
      "Doors", "Windows", "Something else"
    ]
  },
  {
    name: "Kitchen",
    imageUrl: "/images/kitchen.jpg",
    jobTypes: [
      "Tap", "Leaks", "Mould", "Sink", "Tiling",
      "Cupboards, drawers, worktops", "Appliances", "Extractor fan",
      "Lighting", "Windows", "Smoke alarms, carbon monoxide or heat detectors",
      "Doors", "Something else"
    ]
  },
  {
    name: "Heating, Water & Electricity",
    imageUrl: "/images/heating.jpg",
    jobTypes: [
      "Central heating", "Thermostat", "Boiler", "Cold or no hot water",
      "Radiators", "Power cut or no electricity", "Socket", "Plug",
      "Electrical fittings", "Light fittings", "Smoke alarms, carbon monoxide or heat detectors",
      "Something else"
    ]
  },
  {
    name: "Roof, Chimney and Brickwork",
    imageUrl: "/images/roof.jpg",
    jobTypes: [
      "Roof", "Guttering", "Chimney", "Brickwork repointing",
      "External walls", "Rendering", "Fence", "Garden Wall",
      "Something else"
    ]
  },
  {
    name: "Internal communal areas",
    imageUrl: "/images/communal.jpg",
    jobTypes: [
      "Door entry system", "Internal communal doors",
      "Communal lighting", "Internal flooring", "Internal decoration",
      "Window", "Lifts", "Security", "Something else"
    ]
  },
  {
    name: "Outdoor communal areas",
    imageUrl: "/images/outdoor.jpg",
    jobTypes: [
      "Car park", "Paths and paving", "Fencing", "Garden and landscaping",
      "Bin stores", "External lighting", "Something else"
    ]
  }
]

const ReportARepair: React.FC = () => {
  // State management
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedJobType, setSelectedJobType] = useState('')
  const [additionalInformation, setAdditionalInformation] = useState('')
  const [homeNumber, setHomeNumber] = useState('')
  const [mobileNumber, setMobileNumber] = useState('')
  const [preferredAppointmentDate, setPreferredAppointmentDate] = useState('')
  const [preferredAppointmentTime, setPreferredAppointmentTime] = useState('No Preference')
  const [showRepairForm, setShowRepairForm] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null)
  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({})
  
  // Refs for scroll behavior
  const categoriesSectionRef = useRef<HTMLDivElement>(null)
  const repairFormSectionRef = useRef<HTMLDivElement>(null)

  //const currentTenancyAddress = "48 Fairfield Terrace, Leeds"
  const appointmentTimes = ["No Preference", "AM", "PM"]

  // Repair categories data - exactly matching C# model
  // Toggle category expansion for Material-UI Collapse
  const toggleCategory = (categoryName: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }))
  }

  // Get icon component for category - Material-UI icons
  const getIconComponent = (categoryName: string) => {
    switch (categoryName) {
      case "Bathroom": return <BathtubIcon />
      case "Kitchen": return <KitchenIcon />
      case "Heating, Water & Electricity": return <BoltIcon />
      case "Roof, Chimney and Brickwork": return <HomeIcon />
      case "Internal communal areas": return <BusinessIcon />
      case "Outdoor communal areas": return <ParkIcon />
      default: return <BuildIcon />
    }
  }

  // Filter categories based on search term
  const filteredCategories = repairCategories.filter(category => {
    if (!searchTerm.trim()) return true
    
    const searchLower = searchTerm.toLowerCase()
    const categoryMatch = category.name.toLowerCase().includes(searchLower)
    const jobTypeMatch = category.jobTypes.some(jobType => 
      jobType.toLowerCase().includes(searchLower)
    )
    
    return categoryMatch || jobTypeMatch
  })

  // Handle search input with debouncing and auto-expand
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    
    const timeout = setTimeout(() => {
      filterCategories(value)
    }, 300)
    
    setSearchTimeout(timeout)
  }

  // Filter categories function - matches Razor page behavior exactly
  const filterCategories = (searchTermValue: string) => {
    const searchLower = searchTermValue.toLowerCase().trim()
    
    if (searchLower === '') {
      // When search is cleared, show all categories and collapse them
      showAllCategories()
      setTimeout(() => {
        collapseAllCategories()
      }, 100)
      return
    }

    // First, show/hide categories based on match
    const categories = document.querySelectorAll('.category-item')
    const noResults = document.getElementById('noResults')
    let hasVisibleCategories = false

    categories.forEach(category => {
      const categoryElement = category as HTMLElement
      const categoryName = categoryElement.dataset.category || ''
      const jobTypeButtons = categoryElement.querySelectorAll('.job-type-btn')
      let hasMatch = false

      // Check if category name matches
      if (categoryName.includes(searchLower)) {
        hasMatch = true
      }

      // Check if any job type matches
      if (!hasMatch) {
        jobTypeButtons.forEach(jobBtn => {
          const jobType = (jobBtn as HTMLElement).dataset.jobType || ''
          if (jobType.includes(searchLower)) {
            hasMatch = true
          }
        })
      }

      // Show/hide category based on match
      if (hasMatch) {
        categoryElement.style.display = 'block'
        hasVisibleCategories = true
      } else {
        categoryElement.style.display = 'none'
      }
    })

    // Show/hide no results message
    if (noResults) {
      noResults.style.display = (searchLower !== '' && !hasVisibleCategories) ? 'block' : 'none'
    }

    // Auto-expand matching categories using Material-UI state management
    const categoriesToExpand: string[] = []
    repairCategories.forEach(category => {
      const categoryMatch = category.name.toLowerCase().includes(searchLower)
      const jobTypeMatch = category.jobTypes.some(jobType => 
        jobType.toLowerCase().includes(searchLower)
      )
      
      if (categoryMatch || jobTypeMatch) {
        categoriesToExpand.push(category.name)
      }
    })
    
    // Update expanded categories state to show matching categories
    if (categoriesToExpand.length > 0) {
      setExpandedCategories(prev => {
        const newExpanded = { ...prev }
        categoriesToExpand.forEach(categoryName => {
          newExpanded[categoryName] = true
        })
        return newExpanded
      })
    }
  }

  // Show all categories - matches Razor page exactly
  const showAllCategories = () => {
    const categories = document.querySelectorAll('.category-item')
    const noResults = document.getElementById('noResults')

    categories.forEach(category => {
      (category as HTMLElement).style.display = 'block'
    })

    if (noResults) {
      noResults.style.display = 'none'
    }
  }

  // Collapse all categories using Material-UI state management
  const collapseAllCategories = () => {
    setExpandedCategories({})
  }

  // Clear search - matches Razor page behavior exactly
  const clearSearch = () => {
    setSearchTerm('')
    // Show all categories and collapse them when clearing search
    showAllCategories()
    setTimeout(() => {
      collapseAllCategories()
    }, 50) // Small delay to ensure DOM is ready
  }

  // Select job type - matches Razor page behavior exactly
  const selectJobType = (category: string, jobType: string) => {
    setSelectedCategory(category)
    setSelectedJobType(jobType)
    setErrors({})

    // Show repair form with animation - matches Razor page
    handleShowRepairForm()
  }

  // Show repair form - matches Razor page animation (renamed to avoid conflict)
  const handleShowRepairForm = () => {
    const categoriesSection = categoriesSectionRef.current
    const repairFormSection = repairFormSectionRef.current
    
    if (categoriesSection && repairFormSection) {
      // Hide categories with animation
      categoriesSection.style.opacity = '0'
      categoriesSection.style.visibility = 'hidden'

      setTimeout(() => {
        categoriesSection.style.height = '0'
        categoriesSection.style.overflow = 'hidden'

        // Show repair form
        repairFormSection.style.height = 'auto'
        repairFormSection.style.overflow = 'visible'
        repairFormSection.style.opacity = '1'
        repairFormSection.style.visibility = 'visible'
        repairFormSection.classList.add('show')

        setShowRepairForm(true)

        // Scroll to top of form
        repairFormSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 300)
    } else {
      setShowRepairForm(true)
    }
  }

  // Show categories - matches Razor page animation
  const handleShowCategories = () => {
    const categoriesSection = categoriesSectionRef.current
    const repairFormSection = repairFormSectionRef.current
    
    if (categoriesSection && repairFormSection) {
      // Hide repair form
      repairFormSection.style.opacity = '0'
      repairFormSection.style.visibility = 'hidden'
      repairFormSection.classList.remove('show')

      setTimeout(() => {
        repairFormSection.style.height = '0'
        repairFormSection.style.overflow = 'hidden'

        // Show categories
        categoriesSection.style.height = 'auto'
        categoriesSection.style.overflow = 'visible'
        categoriesSection.style.opacity = '1'
        categoriesSection.style.visibility = 'visible'

        setShowRepairForm(false)

        // Scroll to top of categories
        categoriesSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 300)
    } else {
      setShowRepairForm(false)
    }
  }

  // Go back to categories - matches Razor page behavior exactly
  const goBackToCategories = () => {
    // Clear selections
    setSelectedCategory('')
    setSelectedJobType('')
    
    // Clear form fields - matches exact Razor page behavior
    setAdditionalInformation('')
    setPreferredAppointmentDate('')
    setPreferredAppointmentTime('No Preference')
    setHomeNumber('')
    setMobileNumber('') // Matches current React implementation
    setErrors({})
    
    // Show categories section and hide repair form
    handleShowCategories()
  }

  // Validate form - matches C# model validation
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    // Category and job type validation
    if (!selectedCategory || !selectedJobType) {
      newErrors.general = 'Please select a repair category and job type.'
    }

    // Additional information validation (Required in C# model)
    if (!additionalInformation.trim()) {
      newErrors.additionalInformation = 'The Additional Information field is required.'
    }

    // Mobile number validation (Required in C# model)
    if (!mobileNumber.trim()) {
      newErrors.mobileNumber = 'The Mobile Number field is required.'
    }

    // Preferred appointment date validation (Required in C# model)
    if (!preferredAppointmentDate) {
      newErrors.preferredAppointmentDate = 'The Preferred Appointment Date field is required.'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission - matches Razor page behavior
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (validateForm()) {
      setSuccessMessage('Your repair has been submitted.')
      // Reset everything
      goBackToCategories()
    }
  }

  // Get minimum date (today) - matches Razor page
  const getMinDate = () => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  }

  // Initialize form defaults on mount
  useEffect(() => {
    setHomeNumber('')
    setMobileNumber('')
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout)
      }
    }
  }, [searchTimeout])

  // Success message display - Material-UI implementation
  if (successMessage) {
    return (
      <Box sx={{ minHeight: '400px' }} className="repair-diagnosis-wrapper">
        <Typography variant="h4" component="h2" sx={{ textTransform: 'uppercase', mb: 0 }}>
          Report a repair
        </Typography>
        <Box className="tenancy-info">
          <Box sx={{ 
            bgcolor: 'grey.100', 
            color: 'text.primary', 
            p: 3, 
            borderRadius: 2,
            display: 'inline-block'
          }}>
            {/* {currentTenancyAddress} */}
          </Box>
        </Box>

        <Alert 
          severity="info" 
          onClose={() => setSuccessMessage('')}
          sx={{ mt: 4 }}
        >
          {successMessage}
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: "400px" }} className="repair-diagnosis-wrapper">
      <style>{`
        /* Repair Diagnosis Styles - Scoped to this page only - Matches Razor page exactly */
        .repair-diagnosis-wrapper {
          min-height: 400px;
        }

        .repair-diagnosis-wrapper .tenancy-info .badge {
          font-size: 1rem;
          border: 2px solid #6c757d !important;
        }

        .repair-diagnosis-wrapper .search-form .input-group-lg .form-control {
          font-size: 1.1rem;
        }

        .repair-diagnosis-wrapper .category-item {
          transition: all 0.3s ease;
        }

        .repair-diagnosis-wrapper .category-item:hover {
          transform: translateY(-2px);
        }

        .repair-diagnosis-wrapper .category-toggle {
          color: #190046 !important;
          font-weight: 600;
        }

        .repair-diagnosis-wrapper .category-toggle:hover {
          color: #0d6efd !important;
        }

        .repair-diagnosis-wrapper .category-toggle .fas {
          transition: transform 0.3s ease;
        }

        .repair-diagnosis-wrapper .category-toggle[aria-expanded="true"] .fa-chevron-down {
          transform: rotate(180deg);
        }

        .repair-diagnosis-wrapper .job-type-btn {
          transition: all 0.2s ease;
          border-radius: 8px !important;
          padding: 0.75rem 1rem;
          font-weight: 500;
        }

        .repair-diagnosis-wrapper .job-type-btn:hover {
          background-color: #0d6efd;
          color: white !important;
          border-color: #0d6efd;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(13, 110, 253, 0.3);
        }

        .repair-diagnosis-wrapper .repair-form-section .card {
          border-radius: 12px;
          border: none;
        }

        .repair-diagnosis-wrapper .repair-form-section .card-header {
          border-radius: 12px 12px 0 0 !important;
          padding: 1.5rem;
        }

        .repair-diagnosis-wrapper .repair-form-section .card-body {
          padding: 2rem;
        }

        .repair-diagnosis-wrapper .form-control, 
        .repair-diagnosis-wrapper .form-select {
          border-radius: 8px;
          border: 2px solid #e9ecef;
          padding: 0.75rem 1rem;
          transition: all 0.2s ease;
        }

        .repair-diagnosis-wrapper .form-control:focus, 
        .repair-diagnosis-wrapper .form-select:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }

        .repair-diagnosis-wrapper .btn-success {
          background-color: #28a745;
          border-color: #28a745;
          padding: 0.75rem 2rem;
          font-weight: 600;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .repair-diagnosis-wrapper .btn-success:hover {
          background-color: #218838;
          border-color: #1e7e34;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
        }

        .repair-diagnosis-wrapper .category-image {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }

        .repair-diagnosis-wrapper .intro-section p {
          font-size: 1.1rem;
          line-height: 1.6;
        }

        .repair-diagnosis-wrapper .search-section {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          padding: 2rem 0;
          border-radius: 12px;
          margin-bottom: 2rem;
        }

        /* Alert Styles - Scoped to repair page */
        .repair-diagnosis-wrapper .alert-success {
          border-radius: 12px;
          border: none;
          background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
          padding: 1.5rem;
          font-weight: 500;
        }

        /* No results message */
        .repair-diagnosis-wrapper .no-results {
          text-align: center;
          padding: 3rem 2rem;
          color: #6c757d;
          font-style: italic;
        }

        /* Hide/show sections with smooth transitions */
        .repair-diagnosis-wrapper .categories-section, 
        .repair-diagnosis-wrapper .repair-form-section {
          transition: opacity 0.3s ease, visibility 0.3s ease;
        }

        .repair-diagnosis-wrapper .categories-section.hidden, 
        .repair-diagnosis-wrapper .repair-form-section.hidden {
          opacity: 0;
          visibility: hidden;
          height: 0;
          overflow: hidden;
        }

        .repair-diagnosis-wrapper .repair-form-section {
          opacity: 0;
          visibility: hidden;
          height: 0;
          overflow: hidden;
        }

        .repair-diagnosis-wrapper .repair-form-section.show {
          opacity: 1;
          visibility: visible;
          height: auto;
          overflow: visible;
        }

        /* Bootstrap collapse animations */
        .repair-diagnosis-wrapper .collapse {
          transition: height 0.35s ease;
          overflow: hidden;
        }

        .repair-diagnosis-wrapper .collapse:not(.show) {
          height: 0;
        }

        .repair-diagnosis-wrapper .collapse.show {
          height: auto;
        }

        .repair-diagnosis-wrapper .collapsing {
          height: 0;
          overflow: hidden;
          transition: height 0.35s ease;
        }
      `}</style>

      <Typography variant="h4" gutterBottom sx={{ textTransform: "uppercase" }}>
        Report a repair
      </Typography>
      {/* <div className="tenancy-info">
        <div className="badge bg-light text-dark p-3 rounded-3">
          {currentTenancyAddress}
        </div>
      </div> */}

      <Box sx={{ mb: 4 }} className="intro-section">
        <Box sx={{ maxWidth: 'lg', mx: 'auto', px: { xs: 2, lg: 0 } }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            To report a repair please use our handy toolkit below. Just before
            you do, have you seen the{" "}
            <Link
              href="https://www.accentgroup.org/your-home/repairs-and-maintenance/repairs-how-to-videos/"
              sx={{ 
                fontWeight: 'bold',
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              'how to'
            </Link>{" "}
            videos on our main website? They show how to complete some of the
            simple repairs you are responsible for carrying out in your home.
          </Typography>
          <Typography variant="body1">
            If you need to report an emergency repair, please call us on{" "}
            <Typography component="span" sx={{ fontWeight: 'bold' }}>
              0345 678 0555.
            </Typography>
          </Typography>
        </Box>
      </Box>

      {/* Search and Filter Section */}
      {!showRepairForm && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, lg: 0 } }}>
            <Box sx={{ position: "relative" }}>
              <TextField
                fullWidth
                id="searchInput"
                placeholder="What or where is the fault? E.g. 'the roof' or 'no heating'"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "25px",
                    paddingRight: searchTerm ? "120px" : "16px",
                    "& fieldset": {
                      borderWidth: 2,
                      borderColor: "grey.500",
                    },
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <Button
                        onClick={clearSearch}
                        sx={{
                          position: "absolute",
                          right: 8,
                          minWidth: "80px",
                          borderRadius: "20px",
                          textTransform: "none",
                          color: "text.secondary",
                          border: 1,
                          borderColor: "grey.400",
                          "&:hover": {
                            borderColor: "grey.600",
                            backgroundColor: "grey.50",
                          },
                        }}
                        startIcon={<ClearIcon />}
                      >
                        Clear
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* Repair Categories Section */}
      <Box
        className="categories-section"
        id="categoriesSection"
        ref={categoriesSectionRef}
        sx={{
          display: showRepairForm ? "none" : "block",
          transition: "opacity 0.3s ease, visibility 0.3s ease",
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              {filteredCategories.map((category) => (
                <Box
                  key={category.name}
                  sx={{ mb: 2 }}
                  data-category={category.name.toLowerCase()}
                >
                  <Card sx={{ boxShadow: 2, border: 0 }}>
                    <CardContent sx={{ p: 0 }}>
                      <Button
                        fullWidth
                        onClick={() => toggleCategory(category.name)}
                        sx={{
                          p: 0,
                          textAlign: "left",
                          textTransform: "none",
                          backgroundColor: "grey.50",
                          "&:hover": { backgroundColor: "grey.100" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            p: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              backgroundColor: "grey.100",
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 3,
                              color: "primary.main",
                              fontSize: "1.5rem",
                            }}
                          >
                            {getIconComponent(category.name)}
                          </Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "bold",
                              color: "text.primary",
                              flexGrow: 1,
                            }}
                          >
                            {category.name}
                          </Typography>
                          <ExpandMoreIcon
                            sx={{
                              color: "text.secondary",
                              transform: expandedCategories[category.name]
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.3s",
                            }}
                          />
                        </Box>
                      </Button>
                    </CardContent>

                    <Collapse in={expandedCategories[category.name] || false}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(200px, 1fr))",
                            gap: 1,
                          }}
                        >
                          {category.jobTypes.map((jobType) => (
                            <Button
                              key={jobType}
                              variant="outlined"
                              onClick={() =>
                                selectJobType(category.name, jobType)
                              }
                              sx={{
                                p: 2,
                                textAlign: "left",
                                justifyContent: "flex-start",
                                textTransform: "none",
                                borderColor: "primary.main",
                                color: "primary.main",
                                "&:hover": {
                                  backgroundColor: "primary.50",
                                  borderColor: "primary.dark",
                                },
                              }}
                              data-job-type={jobType.toLowerCase()}
                              data-category={category.name}
                              data-job-name={jobType}
                            >
                              {jobType}
                            </Button>
                          ))}
                        </Box>
                      </CardContent>
                    </Collapse>
                  </Card>
                </Box>
              ))}

              {/* No Results Message */}
              {filteredCategories.length === 0 && searchTerm.trim() && (
                <Box id="noResults">
                  <Card sx={{ boxShadow: 2, border: 0 }}>
                    <CardContent sx={{ textAlign: "center", py: 4 }}>
                      <SearchIcon
                        sx={{
                          fontSize: "4rem",
                          color: "text.secondary",
                          mb: 2,
                        }}
                      />
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        No matching repairs found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        We cannot find a suitable repair based on your search
                        terms. Please revise your search or browse the
                        categories above.
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Repair Form Section */}
      <Box
        className={`repair-form-section${showRepairForm ? " show" : ""}`}
        id="repairFormSection"
        ref={repairFormSectionRef}
        sx={{
          display: showRepairForm ? "block" : "none",
          opacity: showRepairForm ? 1 : 0,
          visibility: showRepairForm ? "visible" : "hidden",
        }}
      >
        <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, lg: 0 } }}>
          <Card sx={{ boxShadow: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{ backgroundColor: "primary.main", color: "white", p: 2 }}
              >
                <Typography variant="h5" component="h4" sx={{ mb: 0 }}>
                  <Button
                    onClick={goBackToCategories}
                    sx={{
                      color: "white",
                      textDecoration: "none",
                      p: 0,
                      textTransform: "none",
                      fontSize: "inherit",
                      fontWeight: "inherit",
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    <Box component="span" id="selectedCategoryJobType">
                      {selectedCategory} - {selectedJobType}
                    </Box>
                    <Box component="strong" sx={{ ml: 1 }}>
                      (change)
                    </Box>
                  </Button>
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Thanks for reporting this issue, to help us further with your
                  enquiry please enter any further information below.
                </Typography>

                <Box component="form" onSubmit={handleSubmit} id="repairForm">
                  {errors.general && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {errors.general}
                    </Alert>
                  )}

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      component="label"
                      htmlFor="additionalInformation"
                      sx={{ fontWeight: "medium", mb: 1, display: "block" }}
                    >
                      Enter any further information here...
                    </Typography>
                    <TextField
                      id="additionalInformation"
                      multiline
                      rows={4}
                      fullWidth
                      placeholder="Please provide details about the repair needed..."
                      value={additionalInformation}
                      onChange={(e) => setAdditionalInformation(e.target.value)}
                      error={!!errors.additionalInformation}
                      helperText={errors.additionalInformation}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 3,
                      mb: 3,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        component="label"
                        htmlFor="preferredAppointmentDate"
                        sx={{ fontWeight: "medium", mb: 1, display: "block" }}
                      >
                        Choose a preferred appointment date:
                      </Typography>
                      <TextField
                        type="date"
                        id="preferredAppointmentDate"
                        fullWidth
                        value={preferredAppointmentDate}
                        onChange={(e) =>
                          setPreferredAppointmentDate(e.target.value)
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          min: getMinDate(),
                        }}
                        error={!!errors.preferredAppointmentDate}
                        helperText={errors.preferredAppointmentDate}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        component="label"
                        htmlFor="preferredAppointmentTime"
                        sx={{ fontWeight: "medium", mb: 1, display: "block" }}
                      >
                        Choose a preferred appointment time:
                      </Typography>
                      <FormControl
                        fullWidth
                        error={!!errors.preferredAppointmentTime}
                      >
                        <Select
                          id="preferredAppointmentTime"
                          value={preferredAppointmentTime}
                          onChange={(e) =>
                            setPreferredAppointmentTime(e.target.value)
                          }
                          displayEmpty
                        >
                          {appointmentTimes.map((time) => (
                            <MenuItem key={time} value={time}>
                              {time}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.preferredAppointmentTime && (
                          <FormHelperText>
                            {errors.preferredAppointmentTime}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Box>
                  </Box>

                  <Typography variant="body1" sx={{ mb: 2 }}>
                    These are the current numbers we hold for you and are what
                    we will use in connection with this repair. Please update
                    any incorrect numbers.
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 3,
                      mb: 3,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        component="label"
                        htmlFor="homeNumber"
                        sx={{ fontWeight: "medium", mb: 1, display: "block" }}
                      >
                        Home number
                      </Typography>
                      <TextField
                        type="tel"
                        id="homeNumber"
                        fullWidth
                        value={homeNumber}
                        onChange={(e) => setHomeNumber(e.target.value)}
                        error={!!errors.homeNumber}
                        helperText={errors.homeNumber}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        component="label"
                        htmlFor="mobileNumber"
                        sx={{ fontWeight: "medium", mb: 1, display: "block" }}
                      >
                        Mobile number
                      </Typography>
                      <TextField
                        type="tel"
                        id="mobileNumber"
                        fullWidth
                        value={mobileNumber}
                        onChange={(e) => setMobileNumber(e.target.value)}
                        error={!!errors.mobileNumber}
                        helperText={errors.mobileNumber}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      pt: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={goBackToCategories}
                      sx={{ mr: 2 }}
                    >
                      Back to Categories
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="success"
                      size="large"
                      sx={{ px: 4 }}
                    >
                      Submit this repair
                    </Button>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}

export default ReportARepair